/**
 * JobForm — "Job Details" form panel.
 * Fully controlled — all state lives in the parent (AddJobGigPage).
 *
 * Props:
 *   data            { title, budget, startDate, duration, location, workType, workMode, description, objectives, criteria }
 *   onFieldChange   (key, value) => void
 *   tags            string[]
 *   onTagsChange    (tags) => void
 *   checks          boolean[3]
 *   onToggleCheck   (index) => void
 *   notice          { show: bool, text: string }
 *   onChangeTemplate () => void   — reopens the template modal
 *   onSaveDraft, onDiscard, onPublish
 */
import {
  IconBriefcase,
  IconSparkles,
  IconDeviceFloppy,
  IconX,
  IconSend,
} from "@tabler/icons-react";
import styles from "./jobForm.module.css";
import Button from "../../../components/UI/Button/button";
import Input, {
  Select,
  Textarea,
} from "../../../components/UI/FormInput/input";
import FormSection from "./FormSection";
import TagInput from "../../../components/UI/TagsInput/TagsInput";
import { ALL_SKILLS } from "../../../constants";
import ChecklistOption from "./ChecklistOption";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useContext } from "react";
import { UserContext } from "../../../context";
import { locationSelectStyle } from "../../../utils/functions";
import { Link } from "react-router-dom";

const JOB_COMMITMENTS = [
  {
    label:
      "Sign a commitment to remain in the role for the full contract duration",
    field: "longTermJob",
  },
  {
    label: "Provide at least 1 month's notice before quitting",
    field: "provideOneMonthNotice",
  },
  {
    label: "Has no confirmed relocation plans during the contract period",
    field: "hasNoRelocationPlans",
  },
];

const JobForm = ({
  data,
  notice,
  onChangeTemplate,
  onSaveDraft,
  activeTab,
  setData,
  handleSubmit,
  handleDurationChangeSimple,
  handleDurationBlur,
  loading,
  locationValue,
  setLocationValue,
  allTalentRoles,
  roleLoading,
  draftLoading,
  isEdit,
  isDraft
}) => {
  const { GOOGLE_API_KEY } = useContext(UserContext);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const activeJobType = activeTab === "job" ? "Job" : "Gig";

  return (
    <div>
      {/* Template applied notice */}
      {notice.show && (
        <div className={styles.tmNotice}>
          <IconSparkles size={16} />
          <span className={styles.tmNoticeTxt}>{notice.text}</span>
          <button className={styles.tmChange} onClick={onChangeTemplate}>
            Change template
          </button>
        </div>
      )}

      <div className={styles.formCard}>
        {/* Card header */}
        <div className={styles.formCardHeader}>
          <div
            className={styles.fchIcon}
            style={{ background: "#eef2ff", color: "#1d4ed8" }}
          >
            <IconBriefcase size={21} />
          </div>
          <div>
            <div className={styles.fchTitle}>{activeJobType} Details</div>
            <div className={styles.fchSub}>
              Fill in the information below. Fields marked * are required.
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.formBody}>
            {/* ── Basic Information ── */}
            <FormSection title="Basic Information">
              <div className={`${styles.row} ${styles.row2}`}>
                <Select
                  label="Role"
                  required
                  id="role"
                  name="role"
                  size="lg"
                  placeholder="Select a role..."
                  value={data.role}
                  onChange={handleChange}
                >
                  <option value="">
                    {roleLoading ? "Loading roles..." : "Select a role..."}
                  </option>

                  {allTalentRoles?.map((role) => (
                    <option key={role?._id} value={role?._id}>
                      {role?.rolename}
                    </option>
                  ))}
                </Select>
                <Input
                  label={`${activeJobType} Title`}
                  required
                  id="jobTitle"
                  name="title"
                  size="lg"
                  type="text"
                  placeholder={`e.g. Senior Software Engineer`}
                  value={data.title}
                  onChange={handleChange}
                />
              </div>

              <div className={`${styles.row} ${styles.row2}`}>
                <Input
                  label={`${activeJobType === "Job" ? "Salary / Budget" : "Gig Budget"}`}
                  required
                  id="jobBudget"
                  name="budget"
                  size="lg"
                  type="number"
                  placeholder="e.g. 2000000"
                  leftIcon={<span className={styles.currencyPrefix}>₦</span>}
                  value={data.budget}
                  onChange={(e) =>
                    setData((prevState) => ({
                      ...prevState,
                      budget: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                />
                <Input
                  label="Proposed Start Date"
                  required
                  id="jobStartDate"
                  name="startDate"
                  size="lg"
                  type="date"
                  value={data.startDate}
                  onChange={handleChange}
                />
              </div>

              <div className={`${styles.row} ${styles.row2}`}>
                {activeJobType === "Job" ? (
                  <Input
                    label="Contract Duration"
                    required
                    id="jobDuration"
                    name="duration"
                    size="lg"
                    type="text"
                    placeholder="e.g. 1 year, 2 years"
                    value={data.duration}
                    // onChange={handleChange}
                    onChange={handleDurationChangeSimple}
                    onBlur={handleDurationBlur}
                  />
                ) : (
                  <Select
                    label="Timeline"
                    required
                    id="jobDuration"
                    name="duration"
                    type="text"
                    placeholder="Select timeline..."
                    value={data.duration}
                    onChange={handleChange}
                  >
                    <option value="">Select timeline...</option>
                    <option value="less-than-week">Less than 1 week</option>
                    <option value="1-2-weeks">1 - 2 weeks</option>
                    <option value="1 month">1 month</option>
                    <option value="2 months">2 months</option>
                    <option value="3 months">3 months</option>
                    <option value="6 months">Up to 6 months</option>
                  </Select>
                )}
                <div>
                  <label
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "var(--navy)",
                      display: "block",
                      marginBottom: 7,
                    }}
                  >
                    Location <span style={{ color: "#dc2626" }}>*</span>
                  </label>

                  <GooglePlacesAutocomplete
                    apiKey={GOOGLE_API_KEY}
                    selectProps={{
                      styles: locationSelectStyle,
                      name: "currentLocation",
                      placeholder: "Type in your location...",
                      value: locationValue,
                      onChange: (e) => {
                        setLocationValue(e);
                        setData((prevState) => ({
                          ...prevState,
                          location: e.label ?? "",
                        }));
                      },
                      isSearchable: true,
                      onKeyDown: (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      },
                    }}
                  />
                </div>
              </div>

              <div className={`${styles.row} ${styles.row2} !mb-5`}>
                <Select
                  label="Work Type"
                  required
                  id="jobWorkType"
                  name="type"
                  size="lg"
                  value={data.type}
                  onChange={handleChange}
                >
                  <option value="">Select work type…</option>
                  <option value="Full-Time">Full-Time</option>
                  <option value="Part-Time">Part-Time</option>
                  <option value="Internship">Internship</option>
                </Select>

                <div>
                  <Select
                    label="Work Mode"
                    required
                    size="lg"
                    value={data.workMode}
                    tooltip="Remote: fully remote. Onsite: office-based. Hybrid: mix of both. Relocation: candidate must relocate."
                    name="workMode"
                    onChange={handleChange}
                  >
                    <option value="">Select work mode…</option>
                    <option value="Remote">Remote</option>
                    <option value="Onsite">Onsite</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Relocation">Relocation</option>
                  </Select>
                </div>
              </div>
            </FormSection>

            {/* ── Job Details ── */}
            <FormSection title="Job Details">
              <div className={`${styles.row} ${styles.row1}`}>
                <Textarea
                  label="Description"
                  required
                  id="jobDesc"
                  name="description"
                  size="md"
                  maxWords={250}
                  placeholder="Describe the role, responsibilities, and what success looks like…"
                  value={data.description}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.pair}>
                <div>
                  <Textarea
                    label="Objectives"
                    required
                    tooltip="List the key outcomes this hire should achieve. One item per line."
                    id="objectives"
                    name="objectives"
                    placeholder={
                      "e.g.\nBuild scalable backend services\nCollaborate with the product team\nWrite and maintain unit tests"
                    }
                    maxLength={5000}
                    value={data.objectives}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Textarea
                    label="Acceptance Criteria"
                    required
                    tooltip='Define what "done" looks like. How will you measure success in this role?'
                    id="acceptanceCriteria"
                    name="acceptanceCriteria"
                    placeholder={
                      "e.g.\nAll features pass unit tests\nCode coverage above 80%\nAPI response under 200ms"
                    }
                    maxLength={5000}
                    value={data.acceptanceCriteria}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </FormSection>

            {/* ── Skills & Requirements ── */}
            <FormSection title="Skills & Requirements">
              <div className={`${styles.row} ${styles.row1}`}>
                <div>
                  <label
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "var(--navy)",
                      display: "block",
                      marginBottom: 7,
                    }}
                  >
                    Skills & Requirements{" "}
                    <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <TagInput
                    tags={data.skillsAndRequirements}
                    onChange={(tags) => {
                      setData((prevState) => ({
                        ...prevState,
                        skillsAndRequirements: tags,
                      }));
                    }}
                    suggestions={ALL_SKILLS}
                    placeholder="Type a skill and press Enter…"
                  />
                </div>
              </div>
            </FormSection>

            {/* ── Talent Commitments ── */}
            <FormSection title="Talent Commitments">
              <p className={styles.sectionHint}>
                Would you require the talent to meet these conditions?
              </p>
              <div className={styles.chkList}>
                {JOB_COMMITMENTS.map(({ label, field }) => (
                  <ChecklistOption
                    key={field}
                    label={label}
                    checked={!!data[field]}
                    onToggle={() =>
                      setData((prev) => ({
                        ...prev,
                        [field]: !prev[field],
                      }))
                    }
                  />
                ))}
              </div>
            </FormSection>
          </div>

          {/* Footer */}
          <div className={styles.formFooter}>
            {(!isEdit || isDraft) && (
              <div className={styles.ffLeft}>
                <Button
                  variant="navy"
                  leftIcon={<IconDeviceFloppy size={15} />}
                  onClick={onSaveDraft}
                  disabled={loading || draftLoading}
                  loading={draftLoading}
                >
                  Save as Draft
                </Button>
              </div>
            )}
            <div className={styles.ffRight}>
              <Button
                as={Link}
                to="/client-jobs"
                variant="outlineBlue"
                leftIcon={<IconX size={15} />}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                leftIcon={<IconSend size={15} />}
                loading={loading}
                disabled={loading || draftLoading}
                type="submit"
              >
                Publish
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;
